/**
 * Ejecutor local del contrato de grilla (modo local de `sc-table`): resuelve la consulta sobre todas
 * las filas y devuelve sólo la página. Es el gemelo de `runGridQueryInMemory` de la API y da lo mismo
 * que él y que el ejecutor Mongo (suite de conformidad: conformance.json, copia de la de la API).
 */
import {
  compareKeys,
  compareValues,
  getValue,
  groupKey,
  isEmpty,
  rowComparator,
  toInstant,
} from './compare';
import { buildDateTree, datePartsIn, GRID_TIMEZONE } from './dates';
import { normalizeNeedle, textContains } from './grid-text';
import {
  normalizeGridQuery,
  type GridFieldType,
  type GridFilter,
  type GridQuery,
  type GridResult,
  type GridSpec,
  type NormalizedGridQuery,
} from './grid-query';

type Test = (row: unknown) => boolean;

/** Igualdad exacta de `in`, `eq` y `groupPath`: `null` es vacío; las fechas, por instante. */
function sameValue(cell: unknown, value: unknown, type?: GridFieldType): boolean {
  if (value === null) return isEmpty(cell);
  if (isEmpty(cell)) return false;
  if (type === 'date') {
    const instant = toInstant(cell);
    return !Number.isNaN(instant) && instant === toInstant(value);
  }
  return cell === value;
}

/** ¿El valor es del tipo del campo? (`range` sólo compara valores del tipo, como Mongo). */
function ofType(cell: unknown, type?: GridFieldType): boolean {
  if (isEmpty(cell)) return false;
  if (type === 'number') return typeof cell === 'number';
  if (type === 'date') return !Number.isNaN(toInstant(cell));
  if (type === 'boolean') return typeof cell === 'boolean';
  return typeof cell === 'string';
}

/** Prueba de un filtro sobre una fila, o `null` si no restringe nada. */
function filterTest({ field, op, values }: GridFilter, type?: GridFieldType): Test | null {
  const cellOf = (row: unknown) => getValue(row, field);
  switch (op) {
    case 'in':
    case 'eq': {
      const accepted = op === 'eq' ? values.slice(0, 1) : values;
      return (row) => accepted.some((value) => sameValue(cellOf(row), value, type));
    }
    case 'range': {
      const [from = null, to = null] = values;
      if (from === null && to === null) return null;
      return (row) => {
        const cell = cellOf(row);
        return (
          ofType(cell, type) &&
          (from === null || compareValues(cell, from, type) >= 0) &&
          (to === null || compareValues(cell, to, type) < 0)
        );
      };
    }
    case 'contains': {
      const needle = normalizeNeedle(values[0]);
      return needle ? (row) => textContains(cellOf(row), needle) : null;
    }
    default:
      throw new Error(`Operador de filtro desconocido: ${op as string}`);
  }
}

/** Búsqueda, filtros (menos los de `except`) y, si se pide, las claves de `groupPath`. */
function rowPredicate(
  query: NormalizedGridQuery,
  spec: GridSpec,
  { except, withGroupPath }: { except?: string; withGroupPath: boolean },
): Test {
  const typeOf = (field: string) => spec.fields[field]?.type;
  const tests: Test[] = [];
  const needle = normalizeNeedle(query.search);
  const searchable = Object.keys(spec.fields).filter((field) => spec.fields[field].search);
  if (needle && searchable.length) {
    tests.push((row) => searchable.some((field) => textContains(getValue(row, field), needle)));
  }
  for (const filter of query.filters) {
    if (filter.field === except) continue;
    const test = filterTest(filter, typeOf(filter.field));
    if (test) tests.push(test);
  }
  if (withGroupPath) {
    query.groupPath.forEach((key, level) => {
      const field = query.groupBy[level];
      tests.push((row) => sameValue(getValue(row, field), key, typeOf(field)));
    });
  }
  return (row) => tests.every((test) => test(row));
}

/** `[{ value, count }]` por valor exacto del campo, en el orden en que aparecen. */
function countByValue(rows: readonly unknown[], field: string, type?: GridFieldType) {
  const counts = new Map<unknown, number>();
  for (const row of rows) {
    const key = groupKey(getValue(row, field), type);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts].map(([value, count]) => ({ value, count }));
}

function pageOf<T>(list: T[], { skip, pageSize }: NormalizedGridQuery): T[] {
  return pageSize ? list.slice(skip, skip + pageSize) : list.slice(skip);
}

/**
 * Resuelve `query` sobre `rows` (sin modificarlas):
 * - `rows` → `{ rows, total }` (las filas originales, ordenadas y paginadas);
 * - `groups` → `{ groups: [{ key, count }], total }`;
 * - `facet` → `{ values }`.
 */
export function applyGridQuery<T>(
  rows: readonly T[],
  query: Partial<GridQuery>,
  spec: GridSpec,
  options: { timezone?: string } = {},
): GridResult<T> {
  const q = normalizeGridQuery(query, spec);
  const timezone = options.timezone ?? GRID_TIMEZONE;

  if (q.view === 'facet') {
    const field = q.facetField ?? '';
    const type = spec.fields[field]?.type;
    const subset = rows.filter(rowPredicate(q, spec, { except: field, withGroupPath: false }));
    if (type === 'date') {
      return {
        values: buildDateTree(subset.map((row) => datePartsIn(getValue(row, field), timezone))),
      };
    }
    const values = countByValue(subset, field, type);
    return { values: values.sort((a, b) => compareKeys(a.value, b.value, type)) };
  }

  const filtered = rows.filter(rowPredicate(q, spec, { withGroupPath: true }));

  if (q.view === 'groups') {
    const field = q.groupBy[q.groupPath.length];
    if (!field) return { groups: [], total: 0 };
    const type = spec.fields[field]?.type;
    const dir = q.sort.find((item) => item.field === field)?.dir ?? 'asc';
    const groups = countByValue(filtered, field, type)
      .map(({ value, count }) => ({ key: value, count }))
      .sort((a, b) => compareKeys(a.key, b.key, type, dir));
    return { groups: pageOf(groups, q), total: groups.length };
  }

  const sorted = filtered.slice().sort(rowComparator(q.sort, spec));
  return { rows: pageOf(sorted, q), total: sorted.length };
}
